import { useState, useRef, useEffect, ImgHTMLAttributes } from "react";

import ExclamationTriangleIcon from "@heroicons/react/20/solid/ExclamationTriangleIcon";
import PhotoIcon from "@heroicons/react/20/solid/PhotoIcon";
import WrenchIcon from "@heroicons/react/20/solid/WrenchIcon";
import { useRouter } from "next/router";

import TertiaryButton from "components/button/tertiaryButton/TertiaryButton";
import { useGetDisplayMode } from "queries/legacy/admin-queries";
import { useGetToc } from "queries/legacy/product-queries";
import { Page } from "types/queries";

import styles from "./PageImage.module.scss";

type PageImageProps = {
  loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
  page: Page;
  pageNumber: number;
};

export default function PageImage({
  loading = "lazy",
  page,
  pageNumber,
}: PageImageProps) {
  const router = useRouter();

  const { data: toc } = useGetToc();
  const { data: displayMode } = useGetDisplayMode();

  // Use imageSrc to preload the new page before clearing the old one
  const [imageSrc, setImageSrc] = useState(page.url);
  const [imageState, setImageState] = useState<"default" | "loading" | "error">(
    "loading"
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const preloadPage = async () => {
      if (imageState === "loading") {
        setImageSrc(page.url);
        return;
      }

      setImageState("loading");

      try {
        const preloadImage = new Image();

        preloadImage.src = addOptimalWidthParam(page.url);
        await preloadImage.decode();

        setImageSrc(page.url);
      } catch (error) {
        onImageError();
      }
    };

    if (imageSrc !== page.url) {
      preloadPage();
    }
  }, [page.url, imageSrc, imageState]);

  function onImageError() {
    setImageState("error");
  }

  /**
   * Add the ideal width param to the provided URL
   *
   * This *will fail* if `toc` is called in SSR due to `window` being unavailable.
   */
  function addOptimalWidthParam(url: string) {
    // How big should the image ideally be on this device and window size?
    const desiredWidth =
      (wrapperRef.current?.clientWidth ?? 320) * window.devicePixelRatio;

    // Figure out which size `srcSet` will want so we preload the right image
    const closestSize =
      [640, 750, 828, 1080, 1200].find((size) => size > desiredWidth) ?? 1200;

    return url + "&_width=" + closestSize;
  }

  let pageType:
    | "cover-right"
    | "cover-left"
    | "page-left"
    | "page-right"
    | undefined;
  if (pageNumber === 0) {
    pageType = "cover-right";
  } else if (pageNumber === (toc ?? []).length - 1 && (toc ?? []).length >= 5) {
    pageType = "cover-left";
  } else if (pageNumber % 2 === 0) {
    pageType = "page-right";
  } else {
    pageType = "page-left";
  }

  return (
    <div
      ref={wrapperRef}
      className={[styles.page, styles[pageType], styles[imageState]].join(" ")}
    >
      {/* Error in default display mode */}
      {imageState === "error" && displayMode !== "diagnostic" && (
        <div className={styles.errorWrapper}>
          <div className={styles.imageErrorIcon}>
            <ExclamationTriangleIcon className={styles.errorWarningIcon} />
            <PhotoIcon className={styles.errorPhotoIcon} />
          </div>

          <p>
            Sorry, we faced an error showing this page{" "}
            {page.info?.pageName && <>({page.info.pageName})</>}.
          </p>
          <p>It may help to reload.</p>
          <p>If the problem persists, please contact support.</p>

          <TertiaryButton color="red" onClick={() => router.reload()}>
            Reload page
          </TertiaryButton>
        </div>
      )}

      {/* Error in diagnostic display mode */}
      {imageState === "error" && displayMode === "diagnostic" && (
        <div className={styles.errorWrapper}>
          <div className={styles.imageErrorIcon}>
            <WrenchIcon className={styles.errorPhotoIcon} />
          </div>

          <code>
            <p className={styles.diagnosticText}>Requested URL:</p>
            <p className={styles.diagnosticText}>{page.url}</p>
          </code>
        </div>
      )}

      {imageState !== "error" && wrapperRef.current && (
        <img
          alt=""
          className={styles.image}
          loading={loading}
          onError={onImageError}
          onLoad={() => setImageState("default")}
          src={addOptimalWidthParam(imageSrc)}
          {...(page.info?.pageName
            ? { "data-testid": `page-${page.info.pageName}` }
            : {})}
        />
      )}
    </div>
  );
}
